import styled from "styled-components"
import ReactECharts from "echarts-for-react"
import {EChartsOption, LegendComponentOption, PieSeriesOption} from "echarts"
import "echarts/theme/shine"
import "echarts/theme/blue"
import "echarts/theme/infographic"
import "echarts/theme/london"
import "echarts/theme/dark-blue"
import {useRootStore} from "store/useRootStore"
import {observer} from "mobx-react"
import {useFindRecentLogCounter} from "hooks/queries/path-tracking.query"
import {RecentLogCounter} from "dto/path-tracking/log-counter"

interface ClassificationData {
    name: string
    value: number
}

export const StatusCharts = observer(() => {
    const {dashboardStore: {counter, showEventCount, setShowEventCount}} = useRootStore()
    const {data: recentLogCounters} = useFindRecentLogCounter()
    // console.log("recent log counter ; ", recentLogCounters)
    const workingData: ClassificationData[] = [
        {name: "기타", value: counter.ttlCount - counter.workingCount},
        {name: "동작중 ", value: counter.workingCount},
    ]
    const statusData: ClassificationData[] = [
        {name: "긴급상황 ", value: counter.emergencyCount},
        {name: "오류 ", value: counter.errorCount},
        {name: "정상 ", value: counter.normalCount},
    ]

    const workingDevicePieOption: EChartsOption = {
        legend: legendStyle,
        series: [getPieOption(workingData)]
    }
    const statusPieOption: EChartsOption = {
        legend: legendStyle,
        series: [getPieOption(statusData)]
    }

    const halfChartWidth = 250
    const chartContainerWidth = halfChartWidth * 2 + 10
    return (
        <div style={{position: 'relative'}}>
            <div style={{display: 'flex'}}>
                <ChartsContainer width={halfChartWidth}>
                    <ChartPanel>
                        <ChartHeader>동작 디바이스</ChartHeader>
                        <ReactECharts option={workingDevicePieOption} theme="shine" style={pieChartStyle}
                                      opts={{renderer: 'canvas', width: 'auto'}}/>
                    </ChartPanel>
                </ChartsContainer>
                <ChartsContainer width={halfChartWidth}>
                    <ChartPanel>
                        <ChartHeader>디바이스 상태</ChartHeader>
                        <ReactECharts option={statusPieOption} theme="shine" style={pieChartStyle}
                                      opts={{renderer: 'canvas', width: 'auto'}}/>
                    </ChartPanel>
                </ChartsContainer>
            </div>
            <ChartsContainer width={chartContainerWidth}>
                <ChartPanel>
                    <ChartHeader>최근 10일 이벤트</ChartHeader>
                    <RecentEventChart data={recentLogCounters || []} showEventCount={showEventCount}/>
                </ChartPanel>
                <div style={{position: 'absolute', top: 5, right: 10}} className="has-text-white">
                    <label className="checkbox">
                        <input type="checkbox" checked={showEventCount}
                               onChange={() => setShowEventCount(!showEventCount)}/>
                        이벤트 카운트 보기
                    </label>
                </div>
            </ChartsContainer>

        </div>
    )

})

interface RecentEventChartProps {
    data: RecentLogCounter[],
    showEventCount: boolean
}

const RecentEventChart = ({data, showEventCount}: RecentEventChartProps) => {
    if (showEventCount) {
        return (
            <ReactECharts option={getBarOption(data || [], true)} style={pieChartStyle}
                          theme={"shine"} opts={{renderer: 'canvas', width: 'auto'}}
            />
        )
    }
    return (
        <div>
        <ReactECharts option={getBarOption(data || [], false)} style={pieChartStyle}
                      theme={"shine"} opts={{renderer: 'canvas', width: 'auto'}}
        />
        </div>
    )
}

const legendStyle: LegendComponentOption = {
    itemGap: 0,
    itemHeight: 8,
    itemWidth: 8,
    show: true,
    bottom: 0,
    itemStyle: {borderCap: "round", borderWidth: 1, borderColor: "#fff"},
    textStyle: {color: "#efefef"},
}

const pieChartStyle = {
    width: '100%',
    marginTop: 15,
    height: 180,
}

interface StatusChartsContainerProps {
    width: number
}

const ChartsContainer = styled.div<StatusChartsContainerProps>`
  position: relative;
  display: flex;
  z-index: 100;
  width: ${props => `${props.width}px`};
  border-radius: 12px;
  background-color: rgba(0, 0, 0, 0.4);
  margin-right: 10px;
  margin-top: 10px;
`

const ChartPanel = styled.div`
  flex: 1;
`

const ChartHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  position: absolute;
  left: 10px;
  top: 3px;
  color: #fff;
`

const getBarOption = (data: RecentLogCounter[], showEventCount: boolean = false) => {
    return {
        grid: {
            top: 30,
            bottom: 20,
            left: 40,
            right: 0
        },
        legend: {
            top: -5,
            show: true,
        },
        xAxis: {
            name: '일자',
            type: 'category',
            nameTextStyle: {
                color: '#fff',
                backgroundColor: '#000',
            },
            data: data.map(dt => dt.regDate.substring(5).replaceAll('-', '/')),
        },
        yAxis: {
            type: 'value'
        },
        tooltip: {
            show: true,
        },
        series: [
            showEventCount && {
                data: data.map(dt => dt.logCount),
                type: 'bar',
                showBackground: true,
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.6)'
                }
            },
            {
                data: data.map(dt => dt.emergencyCount),
                type: 'bar',
                showBackground: true,
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.6)'
                }
            }
        ],
        label: {
            show: true,
            fontSize: 12,
            fontWeight: "bolder",
            height: 30,
            // formatter: "{b}\n{d}% ({c})"
            formatter: "{c}"
        },
        labelLine: {length: 3},
    }
}
const getPieOption = (data: ClassificationData[]): PieSeriesOption => {
    return {
        type: "pie",
        radius: ["20%", "75%"],
        avoidLabelOverlap: false,
        legendHoverLink: false,
        tooltip: {
            show: true,
        },
        startAngle: 120,
        label: {
            show: true,
            position: "inner",
            fontSize: 12,
            fontWeight: "bolder",
            height: 30,
            formatter: "{b}\n{d}% ({c})"
        },
        labelLine: {length: 3},
        itemStyle: {
            borderRadius: 4,
            borderColor: `rgba(0, 0, 0, 0.0)`,
            borderWidth: 5,
        },
        data: data,
    }
}